import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaMapMarkerAlt, FaPaw, FaCalendarAlt } from 'react-icons/fa';
import Icon from './Icon';
import AppointmentModal from './AppointmentModal';
import PhoneIcon from '@mui/icons-material/Phone';

// Cores do tema
const theme = {
  primary: '#FF9D5C',
  secondary: '#4FC3F7',
  text: '#1a237e'
};

// Animação de typing
const typing = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const MapButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background-color: #4285f4;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.15);

  &:hover {
    background-color: #3367d6;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    max-width: 280px;
  }
`;

const BannerSection = styled.section`
  min-height: 70vh;
  height: auto;
  border-bottom: 15px solid #FF9D5C;  
  background: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  margin: 0;
  padding-top: 2rem;
  margin-bottom: -2px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 2rem 0;
    margin-top: 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: 100%;  
  margin: 0 auto;    
  position: relative;
  z-index: 3;
  padding: 5rem 1.5rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    height: auto;
  }
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3.5rem;
  align-items: center;
  justify-content: space-evenly;
  height: 100%;
  position: relative;
  z-index: 5;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 900px) {
    gap: 2rem;
  }
  @media (max-width: 768px) {
   margin-top: 60px;
   flex-direction: column;
   align-items: center;
   width: 100%;
   justify-content: center; 
   height: auto;
  }
`;

const TextContent = styled.div`
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    padding-top: 0;
    max-width: 100%;
    margin: 0 auto;
    padding-bottom: 0;
    height: auto;
    text-align: center;
  }
`;

const TypingContainer = styled.div`
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const TypingLine = styled.div<{ $delay: number }>`
  position: relative;
  font-size: 3rem;
  color: ${theme.text};
  line-height: 1.2;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  margin-bottom: 0.5rem;
  
  animation: ${typing} 1s steps(40, end);
  animation-delay: ${props => props.$delay}s;
  animation-fill-mode: both;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    font-size: 2.2rem;
    white-space: normal;
    overflow: visible;
    animation: none;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  span.highlight-primary {
    color: ${theme.primary};
  }

  span.highlight-secondary {
    color: ${theme.secondary};
  }
`;

const HeroImage = styled.div`
  z-index: 5;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;    
  @media (max-width: 768px) {
    
    align-items: center;
    height: auto;
    margin-bottom: 0;
    
  }

  img {
    width: 100%;
    height: 100%;
    max-width: 520px;
    max-height: 520px;
    object-fit: contain;
    object-position: right bottom;
    margin-bottom: 0;
    display: block;
    @media (max-width: 768px) {
      width: 80vw;
      height: auto;
      max-width: 350px;
      object-position: center;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

const TalkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background-color: #25D366;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.08);

  &:hover {
    background-color: #128C7E;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    max-width: 280px;
  }
`;

const EmergencyButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background-color: #ff4d4d;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(255, 77, 77, 0.15);
  pointer-events: none;

  &:hover {
    background-color: #ff3333;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 77, 77, 0.25);
  }

  @media (max-width: 768px) {
    pointer-events: all;
    width: 100%;
    justify-content: center;
    max-width: 280px;
  }
`;

interface PawDecorationProps {
  $delay: string;
  $top: string;
  $left: string;
  $rotation: string;
}

const PawDecoration = styled.div<PawDecorationProps>`
  position: absolute;
  color: #ff9d5c;
  opacity: 0.1;
  transform: rotate(${(props) => props.$rotation});
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  animation: float 3s ease-in-out ${(props) => props.$delay} infinite;
  z-index: 1;
`;

interface BannerProps {
  id?: string;
}

const Banner: React.FC<BannerProps> = ({ id }) => {
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  return (
    <BannerSection id={id}>
      <Container>
        <BannerContent>
          <TextContent>
            <TypingContainer>
              <TypingLine $delay={0}>
                Seu pet em <span className="highlight-primary">boas mãos,</span>
              </TypingLine>
              <TypingLine $delay={1}>
                com <span className="highlight-primary">atendimento</span> que
              </TypingLine>
              <TypingLine $delay={1}>
                faz a <span className="highlight-primary">diferença</span>!
              </TypingLine>
            </TypingContainer>
            <p>
              Clínica veterinária em Brumado-BA com atendimento especializado e carinhoso para seu pet.
            </p>
            <ButtonContainer>
              <TalkButton onClick={() => { window.gtag_report_conversion(); setIsAppointmentModalOpen(true); }}>
                <Icon icon={FaCalendarAlt} size={20} color="white" />
                Agendar Consulta
              </TalkButton>
              <EmergencyButton href="tel:+5577999441369" onClick={() => window.gtag_report_conversion()}>
                <PhoneIcon style={{ fontSize: 20 }} />
                {isMobile ? "Ligar agora" : "(77) 99944-1369"}
              </EmergencyButton>
              <MapButton 
                href="https://www.google.com/maps/dir/?api=1&destination=-14.207727, -41.677639" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={e => { window.gtag_report_conversion('https://www.google.com/maps/dir/?api=1&destination=-14.207727, -41.677639'); }}
              >
                <Icon icon={FaMapMarkerAlt} size={18} color="white" />
                Ir para a clínica
              </MapButton>
            </ButtonContainer>
          </TextContent>
          <HeroImage>
            <img src="/images/hero.png" alt="Mulher com cachorro" />
          </HeroImage>
        </BannerContent>
      </Container>
      
      {/* Patinhas decorativas */}
      <PawDecoration $delay="0s" $top="20%" $left="10%" $rotation="15deg">
        <Icon icon={FaPaw} size={40} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1s" $top="40%" $left="80%" $rotation="-10deg">
        <Icon icon={FaPaw} size={30} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="2s" $top="70%" $left="20%" $rotation="25deg">
        <Icon icon={FaPaw} size={35} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1.5s" $top="60%" $left="70%" $rotation="-20deg">
        <Icon icon={FaPaw} size={25} color="currentColor" />
      </PawDecoration>

      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
      />
    </BannerSection>
  );
};

export default Banner; 